import { useRef, useEffect, useState } from 'react';
import Medications from '../RevampPatientForms/Medications';
import ProviderHistory from './ProviderHistory';
import SocialHistory from './SocialHistory';
import SleepHistory from './SleepHistory';
import SystemsReview from './SystemsReview';
import { Button, Fade, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import './styles.scss';
import { MedicalHistory } from '../../revamp/types/PatientFormTypes';
import { User } from 'src/revamp/types/UserTypes';

interface FormSelectProps {
  updateHistory: (data: any) => void;
  apptId?: string;
  medicalHistory?: MedicalHistory;
  medicalId?: string;
  onContinue: (user?: User) => void;
  step: string;
}

interface FormElementProps {
  step: string;
}

// Separate scroll button component to handle its own state
const ScrollToBottomButton = ({ targetRef }: { targetRef: React.RefObject<HTMLButtonElement> }) => {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        const isVisible = (
          rect.top >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        );
        setShowButton(!isVisible);
      }
    };

    handleScroll();
    const scrollInterval = setInterval(handleScroll, 1000);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(scrollInterval);
    };
  }, [targetRef]);

  const handleClick = () => {
    targetRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Fade in={showButton}>
      <Button
        variant="contained"
        className="fixed-btn"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon sx={{ color: 'secondary.contrastText' }} />}
      >
        <Typography variant="h6">Jump to Bottom</Typography>
      </Button>
    </Fade>
  );
};

const FormSelect = (props: FormSelectProps) => {
  const { updateHistory, apptId, medicalHistory, medicalId, onContinue } = props;
  const elementRef = useRef<HTMLButtonElement>(null);

  const FormElement = (props: FormElementProps) => {
    switch (props.step) {
      case 'sleep-update':
        return <SleepHistory
          apptId={apptId}
          updateHistory={updateHistory}
          medicalHistory={medicalHistory}
          medicalId={medicalId}
          sleepHistory={medicalHistory?.form?.sleepMedicineHistory || {}}
          onContinue={() => onContinue()}
          continueRef={elementRef}
        />
      case 'social-history':
        return <SocialHistory
          apptId={apptId}
          updateHistory={updateHistory}
          medicalHistory={medicalHistory}
          medicalId={medicalId}
          socialHistory={medicalHistory?.form?.socialHistory || {}}
          onContinue={() => onContinue()}
          continueRef={elementRef}
        />
      case 'medications':
        return <Medications
          apptId={apptId}
          medicalHistory={medicalHistory}
          onContinue={() => onContinue()}
          updateHistory={updateHistory}
          continueRef={elementRef}
        />
      case 'provider':
        return <ProviderHistory
          apptId={apptId}
          updateHistory={updateHistory}
          medicalHistory={medicalHistory}
          medicalId={medicalId}
          providerHistory={medicalHistory?.form?.providerHistory || {}}
          onContinue={() => onContinue()}
          continueRef={elementRef}
        />
      case 'details':
        return <SystemsReview
          updateHistory={updateHistory}
          apptId={apptId}
          medicalHistory={medicalHistory}
          medicalId={medicalId}
          reviewOfSystems={medicalHistory?.form?.reviewOfSystems || {}}
          onContinue={() => onContinue()}
          continueRef={elementRef}
        />
      default:
        return null;
    }
  }

  return <>
    <ScrollToBottomButton targetRef={elementRef} />
    <FormElement step={props.step} />
  </>
}

export default FormSelect